import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for customer-related API calls
 * - Base URL is `${API_BASE_URL}/api/v1`
 */
const customerAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/** Attach stored auth token to outgoing requests when available */
customerAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

/**
 * Handle 401 responses by clearing storage and redirecting to login.
 * This keeps behavior consistent across services that use their own axios instance.
 */
customerAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * CustomersService - wrapper for customer API endpoints
 * - Provides CRUD, activate/deactivate, toggle and import operations
 */
class CustomersService {
  /**
   * Fetch customers with optional pagination and filters
   * Returns { success, data, pagination, total }
   */
  async getCustomers(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      const limit = params.limit ?? 20;
      const page = params.page ?? null;
      const skip =
        params.skip !== undefined
          ? params.skip
          : page !== null
            ? (page - 1) * limit
            : 0;

      queryParams.append('skip', skip);
      queryParams.append('limit', limit);

      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }
      if (params.customer_type) queryParams.append('customer_type', params.customer_type);
      if (params.state) queryParams.append('state', params.state);
      if (params.is_active === true || params.is_active === false) {
        queryParams.append('is_active', params.is_active);
      }

      const response = await customerAPI.get(`/customers?${queryParams.toString()}`);

      const data = response.data;
      console.log('Fetched customers data:', data);
      return {
        success: true,
        data: data?.customers || data?.items || data || [],
        pagination: data?.pagination || {},
        total: data?.total || data?.count || (Array.isArray(data) ? data.length : 0),
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /** Get a single customer by id */
  async getCustomerById(customerId) {
    try {
      const response = await customerAPI.get(`/customers/${customerId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Create a new customer */
  async createCustomer(payload) {
    try {
      const response = await customerAPI.post('/customers', payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Update an existing customer */
  async updateCustomer(customerId, payload) {
    try {
      const response = await customerAPI.put(`/customers/${customerId}`, payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Delete customer by id */
  async deleteCustomer(customerId) {
    try {
      await customerAPI.delete(`/customers/${customerId}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Activate a customer */
  async activateCustomer(customerId) {
    try {
      const response = await customerAPI.patch(`/customers/${customerId}/activate`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Deactivate a customer */
  async deactivateCustomer(customerId) {
    try {
      const response = await customerAPI.patch(`/customers/${customerId}/deactivate`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /** Toggle active/inactive status for customer */
  async toggleCustomerStatus(customerId, isActive) {
    return isActive
      ? this.activateCustomer(customerId)
      : this.deactivateCustomer(customerId);
  }

  /** Export customers as Excel using the server-side endpoint, applying current filters */
  async exportCustomersToExcel(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.customer_type) queryParams.append('customer_type', params.customer_type);
      if (params.state) queryParams.append('state', params.state);
      if (params.is_active === true || params.is_active === false) {
        queryParams.append('is_active', params.is_active);
      }

      const query = queryParams.toString();
      const url = `/customers/export/download${query ? `?${query}` : ''}`;
      const response = await customerAPI.get(url, { responseType: 'blob' });

      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }

  /** Import customers using an uploaded Excel file */
  async importCustomersFromExcel(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await customerAPI.post('/customers/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }
}

const customersService = new CustomersService();
export default customersService;
